import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb/Breadcrumb';
import EditUserModal from './EditUserModal';
import ChangeUserRoleModal from './ChangeUserRoleModal';
import './ManageUsersPage.css';
import notify, { confirmAction } from "../utils/notify";

const ManageUsersPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for modal controls.
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);

  // Function to fetch only System Admin users for the current hotel.
  const fetchUsers = async () => {
    if (!authData?.hotelId) {
      setError("Hotel ID is missing from authentication data.");
      setLoading(false);
      return;
    }
    try {
      // GET endpoint: returns only System Admin users for the specified hotel.
      const response = await axiosInstance.get(`/admin/system-admins?hotelId=${authData.hotelId}`);
      setUsers(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      console.error("Error fetching system admin users:", err);
      setError("Failed to fetch users. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the listed values change; the function is re-created every render and is not a trigger
  }, [authData]);

  // Handler to open the Edit modal.
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  // Handler to open the Change Role modal.
  const handleChangeRole = (user) => {
    setSelectedUser(user);
    setShowChangeRoleModal(true);
  };

  // Handler to delete a System Admin user.
  const handleDeleteUser = async (userId) => {
    if (await confirmAction({ title: "Delete this user?", content: "This cannot be undone.", okText: "Delete", danger: true })) {
      try {
        await axiosInstance.delete(`/admin/system-admins/${userId}`);
        fetchUsers();
      } catch (err) {
        console.error("Error deleting user:", err);
        notify.error("Failed to delete user. Please try again.");
      }
    }
  };

  // Handler to block or unblock a user.
  const handleBlockUser = async (userId, currentStatus) => {
    try {
      // POST endpoint toggles the user's active status.
      await axiosInstance.post(`/admin/block-user/${userId}`, { block: !currentStatus });
      fetchUsers();
    } catch (err) {
      console.error("Error updating user status:", err);
      notify.error("Failed to update user status. Please try again.");
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
  };

  const handleCloseChangeRoleModal = () => {
    setShowChangeRoleModal(false);
    setSelectedUser(null);
  };

  return (
    <div className="manage-users-container">
      <Breadcrumb />

      <h1>Manage System Admins</h1>
      <p>Below is a list of all System Admin users registered for your hotel.</p>

      {loading ? (
        <p className="loading-message">Loading users...</p>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : users.length === 0 ? (
        <p>No System Admin users found.</p>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Date Registered</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.userId}>
                <td>{user.userId}</td>
                <td>{`${user.firstName} ${user.lastName}`}</td>
                <td>{user.email}</td>
                <td>{new Date(user.dateRegistered).toLocaleDateString()}</td>
                <td>{user.isActive ? "Active" : "Blocked"}</td>
                <td className="actions-cell">
                  <button onClick={() => handleEditUser(user)}>Edit</button>
                  <button onClick={() => handleChangeRole(user)}>Change Role</button>
                  <button onClick={() => handleDeleteUser(user.userId)}>Delete</button>
                  <button onClick={() => handleBlockUser(user.userId, user.isActive)}>
                    {user.isActive ? "Block" : "Unblock"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={handleCloseEditModal}
          onUserUpdated={fetchUsers}
        />
      )}

      {/* Change User Role Modal */}
      {showChangeRoleModal && selectedUser && (
        <ChangeUserRoleModal
          user={selectedUser}
          onClose={handleCloseChangeRoleModal}
          onRoleUpdated={fetchUsers}
        />
      )}
    </div>
  );
};

export default ManageUsersPage;
